import React, { type ReactElement, useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';
import TableActions from 'atom-components/table-actions-lite';
import ServerControlledTable from 'atom-components/tables/server-controlled-table';
import type { SortingProps } from 'atom-components/tables/types';
import TooltipItem from 'atom-components/tooltip-item';
import MarkStatusBadge from 'feature-components/badges/mark-status-badge';
import FullName from 'feature-components/full-name';
import ActiveMarkerBadge from 'pages/marking/components/active-marker-badge';
import MarkResponseButton from 'pages/marking/components/mark-response-button';
import { markStatusMessage } from 'pages/marking/helpers/mark.helpers';
import { type BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import { type MeCustomerUser, useMe } from 'services/assist-api/entities/me/me';
import type { TestInstanceResponseMarkWithActiveMarkerAndResponse } from 'services/assist-api/entities/test-instance-response-mark/types';
import { type TestInstanceResponseMarkerWithCustomerUser } from 'services/assist-api/entities/test-instance-response-marker/types';
import type { TestInstanceUserWithCustomerUserAndResponses } from 'services/assist-api/entities/test-instance-user/test-instance-users';
import ChangeActiveMarkerButton from './change-active-marker-button';
import { generateMarkRow } from './marks-for-review-table.helpers';
import SetActiveReviewerButton from './set-active-reviewer-button';
import { type MarkRow } from './types';

type Props = {
  isLoading: boolean;
  marks: TestInstanceResponseMarkWithActiveMarkerAndResponse[];
  markers: TestInstanceResponseMarkerWithCustomerUser[];
  markerOrganisationUsers: BaseCustomerUser[];
  learnerTestInstanceUsers: TestInstanceUserWithCustomerUserAndResponses[];
  reviewerOrganisationUsers: BaseCustomerUser[];
} & SortingProps;

/**
 * Returns the marker that has the same ID as the active marker.
 */
function getMarkerFromActiveMarkerId(
  activeMarkerId: number | undefined, // The activeMarker relation can be missing from mark.
  markers: TestInstanceResponseMarkerWithCustomerUser[]
): TestInstanceResponseMarkerWithCustomerUser | undefined {
  return markers.find((m) => m.id === activeMarkerId);
}

/**
 * Returns the user that has the same ID as the active marker.
 */
function getOrganisationUserFromActiveMarkerId(
  activeMarkerId: number | undefined, // The activeMarker relation can be missing from mark.
  organisationUsers: BaseCustomerUser[],
  markers: TestInstanceResponseMarkerWithCustomerUser[]
): BaseCustomerUser | undefined {
  const marker = getMarkerFromActiveMarkerId(activeMarkerId, markers);
  return organisationUsers.find((ou) => ou.id === marker?.user.id);
}

type ActiveMarkerBadgeWrapperProps = {
  activeMarkerId: number | undefined; // The activeMarker relation can be missing from mark.
  markers: TestInstanceResponseMarkerWithCustomerUser[];
  markerOrganisationUsers: BaseCustomerUser[];
  currentOrganisationUser: MeCustomerUser | undefined;
  isAiMarked: boolean;
};

function ActiveMarkerBadgeWrapper({
  activeMarkerId,
  markers,
  markerOrganisationUsers,
  currentOrganisationUser,
  isAiMarked
}: ActiveMarkerBadgeWrapperProps): ReactElement {
  const activeMarker = getMarkerFromActiveMarkerId(activeMarkerId, markers);
  const activeMarkerOrganisationUser = getOrganisationUserFromActiveMarkerId(
    activeMarkerId,
    markerOrganisationUsers,
    markers
  );

  const displayWarning =
    activeMarkerOrganisationUser &&
    currentOrganisationUser &&
    currentOrganisationUser.id !== activeMarkerOrganisationUser.id;

  return (
    <ActiveMarkerBadge
      displayWarning={displayWarning ?? false}
      activeMarker={activeMarker}
      organisationUser={activeMarkerOrganisationUser}
      isAiMarked={isAiMarked}
    />
  );
}

type ActiveReviewerProps = {
  activeReviewerId: number | undefined;
  reviewerOrganisationUsers: BaseCustomerUser[];
};

function ActiveReviewerInfo({
  activeReviewerId,
  reviewerOrganisationUsers
}: ActiveReviewerProps): ReactElement {
  const activeReviewer = reviewerOrganisationUsers.find(
    (reviewer) => reviewer.id === activeReviewerId
  );
  return (
    <TooltipItem
      tooltip={
        activeReviewer?.user.username === undefined
          ? 'No Username'
          : `@${activeReviewer.user.username}`
      }
    >
      <FullName organisationUser={activeReviewer} />
    </TooltipItem>
  );
}

export default function MarksForReviewTable({
  isLoading,
  marks,
  markers,
  markerOrganisationUsers,
  learnerTestInstanceUsers,
  reviewerOrganisationUsers,
  initialSorting,
  onSort
}: Props): ReactElement {
  // Get the current user.
  const meQuery = useMe();
  const currentOrganisationUser = meQuery.data?.customerUser;

  const columnHelper = createColumnHelper<MarkRow>();
  const columns: ColumnDef<MarkRow>[] = useMemo(
    () => [
      columnHelper.accessor('nsn', {
        header: () => 'NSN',
        size: 100
      }),
      columnHelper.accessor('responseId', {
        header: () => 'Response ID',
        size: 100
      }),
      columnHelper.accessor('mark.id', {
        id: 'markId',
        header: () => 'Mark ID',
        size: 100,
        cell: ({ row }) => <span>{row.original.mark.id}</span>
      }),
      columnHelper.accessor('marker.id', {
        id: 'marker',
        header: () => 'Marker',
        cell: ({ row }) => (
          <TooltipItem
            tooltip={
              row.original.marker.organisationUser?.user.username === undefined
                ? 'No Username'
                : `@${row.original.marker.organisationUser?.user.username}`
            }
          >
            <ActiveMarkerBadgeWrapper
              activeMarkerId={row.original.mark.activeMarker?.id}
              markers={markers}
              markerOrganisationUsers={markerOrganisationUsers}
              currentOrganisationUser={currentOrganisationUser}
              isAiMarked={row.original.mark.aiMarked}
            />
          </TooltipItem>
        )
      }),
      columnHelper.accessor('mark.activeReviewer.id', {
        id: 'reviewer',
        header: () => 'Reviewer',
        size: 100,
        cell: ({ row }) =>
          row.original.mark.activeReviewer?.id && (
            <ActiveReviewerInfo
              activeReviewerId={row.original.mark.activeReviewer?.id}
              reviewerOrganisationUsers={reviewerOrganisationUsers}
            />
          )
      }),
      columnHelper.accessor('status', {
        header: () => 'Status',
        cell: ({ row }) => (
          <TooltipItem tooltip={markStatusMessage(row.original.status)}>
            <MarkStatusBadge>{row.original.status}</MarkStatusBadge>
          </TooltipItem>
        )
      }),
      columnHelper.display({
        id: 'actions',
        header: () => 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <TableActions>
            <MarkResponseButton
              responseId={row.original.mark.testInstanceResponse.id}
              markId={row.original.mark.id}
            />
            <ChangeActiveMarkerButton mark={row.original.mark} />
            <SetActiveReviewerButton mark={row.original.mark} />
          </TableActions>
        ),
        size: 90
      })
    ],
    [
      columnHelper,
      markerOrganisationUsers,
      markers,
      currentOrganisationUser,
      reviewerOrganisationUsers
    ]
  );

  const data: MarkRow[] = useMemo(
    () =>
      marks.map((mark) =>
        generateMarkRow(mark, markers, markerOrganisationUsers, learnerTestInstanceUsers)
      ),
    [markers, markerOrganisationUsers, learnerTestInstanceUsers, marks]
  );

  return (
    <ServerControlledTable
      isLoading={isLoading}
      data={data}
      columns={columns}
      initialSorting={initialSorting}
      onSort={onSort}
      entity="mark"
    />
  );
}
